import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  getCleaningTasksRequest,
  updateCleaningTaskRequest,
} from '../../api/cleaningTaskApi';
import { CleaningTaskResponse } from '../../models/ResponseModels/CleaningTaskResponse';
import { UpdateCleaningTaskRequest } from '../../models/RequestModels/UpdateCleaningTaskRequest';

interface CleaningTaskState {
  cleaningTasks: CleaningTaskResponse[];
  loading: boolean;
  error: string | null;
}

const initialState: CleaningTaskState = {
  cleaningTasks: [],
  loading: false,
  error: null,
};

export const fetchCleaningTasks = createAsyncThunk(
  'cleaningTasks/fetchCleaningTasks',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getCleaningTasksRequest();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || 'Failed to load cleaning tasks.',
      );
    }
  },
);

export const updateCleaningTask = createAsyncThunk(
  'cleaningTasks/updateCleaningTask',
  async (
    payload: { id: number; body: UpdateCleaningTaskRequest },
    { rejectWithValue },
  ) => {
    try {
      const response = await updateCleaningTaskRequest(
        payload.id,
        payload.body,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || 'Failed to update cleaning task.',
      );
    }
  },
);

const cleaningTaskSlice = createSlice({
  name: 'cleaningTasks',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchCleaningTasks.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCleaningTasks.fulfilled, (state, action) => {
        state.loading = false;
        state.cleaningTasks = action.payload;
      })
      .addCase(fetchCleaningTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateCleaningTask.pending, state => {
        state.loading = true;
      })
      .addCase(updateCleaningTask.fulfilled, (state, action) => {
        state.loading = false;
        state.cleaningTasks = state.cleaningTasks.map(task =>
          task.id === action.payload.id ? action.payload : task,
        );
      })
      .addCase(updateCleaningTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default cleaningTaskSlice.reducer;
